import { config } from '../config';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useLoadJson } from '../hooks/useLoadJson';
import { usePublication } from '../hooks/usePublication';
import { findTypeOption, Fragment, FragmentResourceTypes, FragmentType, optionTypeLibro, Publication, PublicationWrapper, Section, SubSection, typeLibro, typeLibrosOptions } from '../interfaces/Publication';
import resourceTypes from '../interfaces/ResourceTypes';
import { translateFragmentType } from '../helpers';

interface LibroLoaderProps {
    id: string | undefined
    chapterNumber: number | undefined
    typeLibro: typeLibro
}

// Memoized function to rename fragments - prevents recreation on every render
const useReNameFragmentsName = () => {
    return useCallback((sections: Section[]): Section[] => {
        let indexTABLE = 0;
        let indexINF = 0;
        let indexVIDEO = 0;
        let indexPERFIL = 0;
        let indexLAB = 0;
        let indexOVA = 0;

        return sections.map((section) => {
            const newSubsection: SubSection[] = [];
            section.subsections?.forEach((subsection) => {
                const newFragments: Fragment[] = [];
                subsection.fragments.forEach(fragment => {
                    if (fragment.type === FragmentType.TABLE) {
                        indexTABLE++;
                        newFragments.push({
                            ...fragment,
                            title: `<b>${translateFragmentType(fragment.type)} ${indexTABLE}.</b> ${fragment.title}`
                        });
                    } else if (fragment.type === FragmentType.INF) {
                        indexINF++;
                        newFragments.push({
                            ...fragment,
                            title: `<b>${translateFragmentType(fragment.type)} ${indexINF}.</b> ${fragment.title}`
                        });
                    } else if (fragment.type === FragmentType.VIDEO) {
                        indexVIDEO++;
                        newFragments.push({
                            ...fragment,
                            title: `<b>${translateFragmentType(fragment.type)} ${indexVIDEO}.</b> ${fragment.title}`
                        });
                    } else if (fragment.type === FragmentType.PERFIL) {
                        indexPERFIL++;
                        newFragments.push({
                            ...fragment,
                            title: `<b>${translateFragmentType(fragment.type)} ${indexPERFIL}.</b> ${fragment.title}`
                        });
                    } else if (fragment.type === FragmentType.LAB) {
                        indexLAB++;
                        newFragments.push({
                            ...fragment,
                            title: `<b>${translateFragmentType(fragment.type)} ${indexLAB}.</b> ${fragment.title}`
                        });
                    } else if (fragment.type === FragmentType.OVA) {
                        indexOVA++;
                        newFragments.push({
                            ...fragment,
                            title: `<b>${translateFragmentType(fragment.type)} ${indexOVA}.</b> ${fragment.title}`
                        });
                    } else {
                        newFragments.push({
                            ...fragment,
                            title: `${fragment.title}`
                        });
                    }
                })
                newSubsection.push({
                    ...subsection,
                    fragments: newFragments
                })
            })
            return {
                ...section,
                subsections: newSubsection
            };
        });
    }, []);
};

// Memoized data processor
const useProcessPublicationData = () => {
    const reNameFragmentsName = useReNameFragmentsName();
    
    return useCallback((dataPub: Publication, chapterNumber: number, shouldRenameFragments: boolean) => {
        // Process sections only if needed
        const processedSections = shouldRenameFragments 
            ? reNameFragmentsName(dataPub.sections)
            : dataPub.sections;

        const processedPublication = {
            ...dataPub,
            sections: processedSections,
            currentSection: chapterNumber || 0
        };

        // Precompute fragments map for quick access (optional - can be done lazily when needed)
        const fragmentMap = new Map();
        dataPub.sections.forEach((section, idxSection) => {
            section.subsections?.forEach((subSection) => {
                subSection.fragments.forEach((fragment) => {
                    fragmentMap.set(fragment.id, { ...fragment, idxSection });
                });
            });
        });

        return { processedPublication, fragmentMap };
    }, [reNameFragmentsName]);
};

// ESTE COMPONENTE SE CREO EXCLUSIVAMENTE PARA ESTAR DENTRO DEL CONTEXTO Y PODER CARGAR LA INFO DEL LIBRO
const LibroLoader = ({ id, chapterNumber, typeLibro }: LibroLoaderProps) => {
    const { changePublication, publication: init_state } = usePublication();
    const processPublicationData = useProcessPublicationData();

    const init_state_publicationWrapper: PublicationWrapper = { data: {} }
    const directory = findTypeOption(typeLibro)?.directory || ""
    const fileName = findTypeOption(typeLibro)?.fileName || ""
    const url: string | null = id ? config.urlJsonPublications + directory + '/' + (Math.floor(+id / 100)) + '/' + id + '/' + fileName + id + '.json' : null;

    const { data, isPending, error } = useLoadJson({ url }, init_state_publicationWrapper)

    // Memoize the processed data to prevent unnecessary processing
    const processedData = useMemo(() => {
        if (id && data?.data?.publication) {
            const dataPub: Publication = data.data.publication;
            const shouldRenameFragments = init_state.sections.length <= 0;
            
            return processPublicationData(dataPub, chapterNumber || 0, shouldRenameFragments);
        }
        return null;
    }, [id, data, chapterNumber, processPublicationData, init_state.sections.length]);

    useEffect(() => {
        if (processedData) {
            changePublication(processedData.processedPublication);
        }
    }, [processedData, changePublication]);

    return <></>;
}

export default LibroLoader;